import React, { useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowRight, ArrowLeft, CheckCircle2, ShieldCheck, Users, Stethoscope, ClipboardCheck, ChevronDown } from "lucide-react";
import { SERVICE_BY_SLUG, SERVICES, COMMON_FAQS } from "../data/services";
import { useSEO, serviceSchema, breadcrumbSchema, SITE_URL } from "../lib/seo";

function FAQRow({ q, a, i }) {
  const [open, setOpen] = useState(i === 0);
  return (
    <div className="alpha-card p-0 overflow-hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-4 p-5 text-left"
        data-testid={`faq-${i}`}
      >
        <span className="font-display text-base font-semibold pr-4">{q}</span>
        <ChevronDown className={`w-5 h-5 shrink-0 text-[color:var(--alpha-mute)] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-5 pb-5 text-sm text-[color:var(--alpha-mute)] leading-relaxed">
          {a}
        </div>
      )}
    </div>
  );
}

export default function ServiceDetailPage() {
  const { slug } = useParams();
  const s = SERVICE_BY_SLUG[slug];
  useSEO({
    title: s ? `${s.title} | Alpha Community Care NDIS Provider` : "Services | Alpha Community Care",
    description: s?.intro,
    canonical: s ? `${SITE_URL}/services/${s.slug}` : `${SITE_URL}/services`,
    jsonLd: s ? [
      serviceSchema({ name: s.title, description: s.intro, slug: s.slug, city: null }),
      breadcrumbSchema([
        { name: "Home", path: "/" },
        { name: "Services", path: "/services" },
        { name: s.short, path: `/services/${s.slug}` },
      ]),
    ] : [],
  });
  if (!s) return <Navigate to="/services" replace />;
  const Icon = s.Icon;
  const related = (s.related || []).map((x) => SERVICE_BY_SLUG[x]).filter(Boolean);

  return (
    <div data-testid={`page-service-${s.slug}`}>
      {/* Hero */}
      <section className="alpha-gradient-hero border-b border-[color:var(--alpha-line)]">
        <div className="alpha-container pt-10 pb-14">
          <Link to="/services" className="inline-flex items-center gap-2 text-sm text-[color:var(--alpha-mute)] hover:text-[color:var(--alpha-purple-700)]">
            <ArrowLeft className="w-4 h-4" /> All services
          </Link>
          <div className="mt-6 grid lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-7">
              <div className="alpha-overline">NDIS · Service</div>
              <div className="mt-4 flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl alpha-gradient text-[color:var(--alpha-purple-50)] flex items-center justify-center shadow-md">
                  <Icon className="w-6 h-6" />
                </div>
                <h1 className="text-3xl sm:text-[44px] font-display font-medium tracking-tight leading-[1.05]">{s.title}</h1>
              </div>
              <p className="mt-5 max-w-2xl text-lg text-[color:var(--alpha-mute)]">{s.intro}</p>
              <div className="mt-7 flex flex-wrap gap-3">
                <Link to="/referrals" className="alpha-btn-primary" data-testid="svc-cta-refer">Make a referral <ArrowRight className="w-4 h-4" /></Link>
                <Link to="/contact" className="alpha-btn-ghost">Talk to intake</Link>
              </div>
            </div>
            <div className="lg:col-span-5 hidden lg:block">
              <div className="alpha-card p-6">
                <div className="alpha-overline">Quick facts</div>
                <ul className="mt-3 space-y-2 text-sm">
                  <li className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[color:var(--alpha-purple-700)]" /> NDIS registered &amp; audited</li>
                  <li className="flex items-center gap-2"><Users className="w-4 h-4 text-[color:var(--alpha-purple-700)]" /> Qualified, verified workforce</li>
                  <li className="flex items-center gap-2"><Stethoscope className="w-4 h-4 text-[color:var(--alpha-purple-700)]" /> Clinical &amp; allied-health collaboration</li>
                  <li className="flex items-center gap-2"><ClipboardCheck className="w-4 h-4 text-[color:var(--alpha-purple-700)]" /> Individualised support plans</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="alpha-section">
        <div className="alpha-container grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8 space-y-10">
            <div>
              <div className="alpha-overline">Who it's for</div>
              <h2 className="mt-2 font-display text-2xl sm:text-3xl font-medium">Designed around real people, real needs.</h2>
              <ul className="mt-5 space-y-3">
                {s.forWho.map((w) => (
                  <li key={w} className="flex items-start gap-3 text-[color:var(--alpha-ink)]"><CheckCircle2 className="w-5 h-5 mt-0.5 text-[color:var(--alpha-purple-600)] shrink-0" /> <span>{w}</span></li>
                ))}
              </ul>
            </div>

            <div>
              <div className="alpha-overline">How support is delivered</div>
              <h2 className="mt-2 font-display text-2xl sm:text-3xl font-medium">Considered, consistent, collaborative.</h2>
              <ul className="mt-5 space-y-3">
                {s.delivery.map((w) => (
                  <li key={w} className="flex items-start gap-3 text-[color:var(--alpha-ink)]"><CheckCircle2 className="w-5 h-5 mt-0.5 text-[color:var(--alpha-purple-600)] shrink-0" /> <span>{w}</span></li>
                ))}
              </ul>
            </div>

            <div className="alpha-card p-7 bg-[color:var(--alpha-purple-50)] border-[color:var(--alpha-line-strong)]">
              <div className="alpha-overline">Key inclusions</div>
              <div className="mt-4 grid sm:grid-cols-2 gap-3">
                {s.inclusions.map((w) => (
                  <div key={w} className="flex items-start gap-2 text-sm"><CheckCircle2 className="w-4 h-4 mt-0.5 text-[color:var(--alpha-purple-700)] shrink-0" /> {w}</div>
                ))}
              </div>
            </div>

            {/* Trust / compliance */}
            <div className="grid sm:grid-cols-2 gap-5">
              <div className="alpha-card p-6">
                <ShieldCheck className="w-6 h-6 text-[color:var(--alpha-purple-700)]" />
                <h3 className="mt-3 font-display text-lg font-semibold">Registered &amp; compliant</h3>
                <p className="text-sm text-[color:var(--alpha-mute)] mt-1">NDIS Practice Standards-aligned, with active quality, safeguarding and clinical governance.</p>
              </div>
              <div className="alpha-card p-6">
                <Users className="w-6 h-6 text-[color:var(--alpha-purple-700)]" />
                <h3 className="mt-3 font-display text-lg font-semibold">Workforce you can trust</h3>
                <p className="text-sm text-[color:var(--alpha-mute)] mt-1">Screened, qualified and supervised — including specialist training for complex needs.</p>
              </div>
            </div>

            {/* FAQ */}
            <div>
              <div className="alpha-overline">FAQ</div>
              <h2 className="mt-2 font-display text-2xl sm:text-3xl font-medium">Common questions.</h2>
              <div className="mt-5 space-y-3">
                {COMMON_FAQS.map((f, i) => <FAQRow key={f.q} {...f} i={i} />)}
              </div>
            </div>
          </div>

          {/* Sidebar CTA */}
          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-32 space-y-5">
              <div className="alpha-card p-6 alpha-gradient text-[color:var(--alpha-purple-50)]">
                <div className="alpha-overline !text-[color:var(--alpha-green-500)]">Start today</div>
                <h3 className="mt-2 font-display text-xl font-semibold">Talk to our intake team</h3>
                <p className="text-sm text-white/85 mt-2">We respond within one business day. Call, email or submit a referral online.</p>
                <div className="mt-5 space-y-2">
                  <Link to="/referrals" className="bg-[color:var(--alpha-purple-50)] text-[color:var(--alpha-purple-800)] rounded-full px-5 py-3 font-semibold text-center block">Make a referral</Link>
                  <a href="tel:1300380692" className="border border-white/60 rounded-full px-5 py-3 font-semibold text-center block hover:bg-white/10">1300 380 692</a>
                </div>
              </div>

              {related.length > 0 && (
                <div className="alpha-card p-6">
                  <div className="alpha-overline">Related services</div>
                  <ul className="mt-3 space-y-2">
                    {related.map((r) => (
                      <li key={r.slug}>
                        <Link to={`/services/${r.slug}`} className="flex items-center justify-between gap-2 p-2 rounded-lg hover:bg-[color:var(--alpha-purple-50)] text-sm" data-testid={`related-${r.slug}`}>
                          <span>{r.short}</span>
                          <ArrowRight className="w-3.5 h-3.5 text-[color:var(--alpha-mute)]" />
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
